/*
Purpose: Slide-over panel showing one session and its per-student actions.
*/

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';

import { DIAGNOSTICS_LANE_COLOR, DISPLAY_STATUS, TUTOR_LANE_COLORS } from '../constants';
import { Avatar, ModeBadge, StatusPill, Tag } from './badges';

/** Statuses where a defer request is already open, so deferring again is a no-op. */
const DEFER_BLOCKED_STATUSES = [
  DISPLAY_STATUS.DEFER_REQUESTED,
  DISPLAY_STATUS.SLOT_ASSIGN,
  DISPLAY_STATUS.CANCELLED,
  DISPLAY_STATUS.FORFEIT,
  DISPLAY_STATUS.WRITEOFF,
  DISPLAY_STATUS.ATTENDED,
];

function actionKey(student, action) {
  return `${student.key}:${action}`;
}

function isSessionStillOpen(session) {
  if (!session?.date || !session?.endTime) {
    return true;
  }

  const timezone = sessionStorage.getItem('timezone_identifier');
  const end = timezone
    ? Moment.tz(`${session.date}T${session.endTime}`, timezone)
    : Moment(`${session.date}T${session.endTime}`);
  const now = timezone ? Moment().tz(timezone) : Moment();

  if (!end.isValid()) {
    return true;
  }

  return !now.isAfter(end);
}

function canReEnrolStudent(session, student) {
  if (student.isReassigned || session.isOneOffClass) {
    return false;
  }
  // One Off Requested keeps Re-enrol available (production shows Reenrol for this state).
  if (student.status === DISPLAY_STATUS.ONE_OFF_REQUESTED) {
    return true;
  }
  return isSessionStillOpen(session);
}

const VOID_STATUSES = [
  DISPLAY_STATUS.CANCELLED,
  DISPLAY_STATUS.FORFEIT,
  DISPLAY_STATUS.WRITEOFF,
];

const WORKFLOW_STATUSES = [
  DISPLAY_STATUS.DEFER_REQUESTED,
  DISPLAY_STATUS.SLOT_ASSIGN,
  DISPLAY_STATUS.ONE_OFF_REQUESTED,
];

/** Year · Regular/One-off/Make-up/Deferred — One-off like production reassigned mark. */
function studentClassLabel(session, student) {
  if (VOID_STATUSES.includes(student.status) || WORKFLOW_STATUSES.includes(student.status)) {
    return null;
  }
  if (session.isMakeUpClass) {
    return 'Make-up';
  }
  if (student.isOneOff || session.isOneOffClass) {
    return 'One-off';
  }
  if (student.isDeferred) {
    return 'Deferred';
  }
  return 'Regular';
}

/** One student row plus its action buttons. */
function StudentCard({
  session,
  student,
  busyActionKey,
  onEditStatus,
  onToggleMode,
  onDefer,
  onOneOff,
  onToggleFlexi,
  onReEnrol,
  onPause,
  onStudentClick,
}) {
  const isBusy = (action) => busyActionKey === actionKey(student, action);
  const anyBusy = Boolean(busyActionKey);

  const sessionOpen = isSessionStillOpen(session);
  const isDeferRequested =
    student.status === DISPLAY_STATUS.DEFER_REQUESTED ||
    student.status === DISPLAY_STATUS.SLOT_ASSIGN;
  const isOneOffRequested = student.status === DISPLAY_STATUS.ONE_OFF_REQUESTED;
  const isPaused = student.isPaused || student.status === DISPLAY_STATUS.PAUSED;
  const isCancelled = student.status === DISPLAY_STATUS.CANCELLED;
  const isForfeitOrWriteoff =
    student.status === DISPLAY_STATUS.FORFEIT || student.status === DISPLAY_STATUS.WRITEOFF;
  // Cancelled / forfeit / writeoff: settled on this session — no Defer / Flexi / One-Off.
  const isSettled = isCancelled || isForfeitOrWriteoff;

  const canDefer = !DEFER_BLOCKED_STATUSES.includes(student.status);
  const canReEnrol = canReEnrolStudent(session, student);
  const canRequestOneOff = !isOneOffRequested;
  const canPause = (sessionOpen || isOneOffRequested) && !isPaused;
  const classLabel = studentClassLabel(session, student);

  // Production: settled or open one-off request → Re-enrol + Pause only.
  const showDefer = !isSettled && !isOneOffRequested;
  const showReEnrol = sessionOpen || isOneOffRequested;
  const showFlexi = !isSettled && !isDeferRequested && !isOneOffRequested;
  const showOneOff = !isSettled && !isDeferRequested && !isOneOffRequested;
  const showPause =
    (sessionOpen || isOneOffRequested) && !isPaused && !student.isReassigned;

  return (
    <div className="cv2-stu">
      <div className="cv2-stu__main">
        <Avatar initials={student.initials} color={student.avatarColor} title={student.name} />

        <span className="cv2-stu__info">
          <button
            type="button"
            className={`cv2-stu__name cv2-stu__name--link${
              student.isStruckThrough ? ' cv2-stu__name--struck' : ''
            }`}
            onClick={() => onStudentClick(student)}
          >
            {student.isReassigned ? '*' : ''}
            {student.name}
          </button>
          <span className="cv2-stu__sub">
            {[student.yearLevel, student.subjectName, classLabel].filter(Boolean).join(' · ')}
            {student.isFlexi ? <Tag variant="flexi">Flexi</Tag> : null}
          </span>
        </span>

        <span className="cv2-stu__badges">
          <StatusPill
            status={student.isPaused ? DISPLAY_STATUS.PAUSED : student.status}
            showDot
            disabled={anyBusy}
            onClick={
              student.isAttendanceEditable ? () => onEditStatus(session, student) : undefined
            }
          />
          <ModeBadge
            mode={student.deliveryMode}
            showLabel={false}
            disabled={anyBusy}
            onClick={() => onToggleMode(session, student)}
          />
        </span>
      </div>

      <div className="cv2-stu__actions">
        {showDefer ? (
          <button
            type="button"
            className="cv2-btn cv2-btn--action-defer"
            disabled={!canDefer || anyBusy}
            onClick={() => onDefer(session, student)}
            title={canDefer ? 'Raise a defer request' : 'Defer is not available for this status'}
          >
            {isBusy('defer') ? 'Saving…' : 'Defer'}
          </button>
        ) : null}
        {showReEnrol ? (
          <button
            type="button"
            className="cv2-btn cv2-btn--action-reenrol"
            disabled={!canReEnrol || anyBusy}
            onClick={() => onReEnrol(session, student)}
            title={
              canReEnrol
                ? 'Move onto another session'
                : 'Re-enrol is not available for this student or session'
            }
          >
            {isBusy('reenrol') ? 'Loading…' : 'Re-enrol'}
          </button>
        ) : null}
        {showFlexi ? (
          <button
            type="button"
            className="cv2-btn cv2-btn--action-flexi"
            disabled={anyBusy}
            onClick={() => onToggleFlexi(session, student)}
          >
            {isBusy('flexi') ? 'Saving…' : student.isFlexi ? 'Unset Flexi' : 'Set Flexi'}
          </button>
        ) : null}
        {showOneOff ? (
          <button
            type="button"
            className="cv2-btn cv2-btn--action-oneoff"
            disabled={!canRequestOneOff || anyBusy}
            onClick={() => onOneOff(session, student)}
            title={
              canRequestOneOff ? 'Request a one-off session' : 'A one-off request is already open'
            }
          >
            {isBusy('oneoff') ? 'Saving…' : 'One-Off'}
          </button>
        ) : null}
        {showPause ? (
          <button
            type="button"
            className="cv2-btn cv2-btn--action-pause"
            disabled={!canPause || anyBusy}
            onClick={() => onPause(session, student)}
            title={canPause ? 'Pause this student for a date range' : 'Student is already paused'}
          >
            {isBusy('pause') ? 'Saving…' : 'Pause'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function SessionDrawer({
  session,
  onClose,
  onEditStatus,
  onToggleMode,
  onDefer,
  onOneOff,
  onToggleFlexi,
  onReEnrol,
  onPause,
  onEditSession,
  onStudentClick,
  onTutorClick,
  busyActionKey,
}) {
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key !== 'Escape') {
        return;
      }
      // Bootstrap modals steal focus; skip closing the drawer while one is open so Escape
      // dismisses the dialog first.
      if (document.querySelector('.modal.show')) {
        return;
      }
      onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!session) {
    return null;
  }

  const laneColor = session.isDiagnostics
    ? DIAGNOSTICS_LANE_COLOR
    : TUTOR_LANE_COLORS[session.laneColorIndex % TUTOR_LANE_COLORS.length];
  const displayDate = Moment(session.date, 'YYYY-MM-DD');
  const notLoggedInCount = session.students.filter(
    (student) => student.status === DISPLAY_STATUS.PENDING
  ).length;
  const subjects = Array.from(
    new Set(session.students.map((student) => student.subjectName).filter(Boolean))
  );
  const timeRange = session.timeRangeLabel;
  const anyBusy = Boolean(busyActionKey);

  return (
    <>
      <button
        type="button"
        className="cv2-drawer-backdrop"
        onClick={onClose}
        aria-label="Close session details"
      />

      <aside className="cv2-drawer" role="dialog" aria-modal="true" aria-label="Session details">
        <header className="cv2-drawer__head">
          {session.isDiagnostics ? (
            <span
              className="cv2-drawer__avatar"
              style={{ background: laneColor.edge }}
              title={session.tutorName || 'Initial Diagnostics'}
            >
              ID
            </span>
          ) : (
            <button
              type="button"
              className="cv2-drawer__avatar"
              style={{ background: laneColor.edge }}
              title={session.tutorName ? `Open ${session.tutorName}` : undefined}
              onClick={() => onTutorClick(session)}
            >
              {session.tutorName ? session.tutorName.slice(0, 2).toUpperCase() : '—'}
            </button>
          )}
          <span className="cv2-drawer__heading">
            <strong className="cv2-drawer__title">
              {session.isDiagnostics ? (
                <span>{session.tutorName || 'Initial Diagnostics'}</span>
              ) : (
                <button
                  type="button"
                  className="cv2-drawer__tutor-link"
                  onClick={() => onTutorClick(session)}
                >
                  {session.tutorName}
                </button>
              )}
              {session.isDiagnostics
                ? ''
                : subjects.length === 1
                  ? ` · ${subjects[0]}`
                  : ''}
            </strong>
            <span className="cv2-drawer__sub">
              <span>
                {displayDate.isValid() ? displayDate.format('ddd DD MMM YYYY') : session.date}
                {timeRange ? ` · ${timeRange}` : ''}
              </span>
              {session.isLive ? (
                <span>
                  <span className="cv2-live-dot" /> Ongoing
                </span>
              ) : null}
            </span>
          </span>
          <button
            type="button"
            className="cv2-drawer__close"
            onClick={onClose}
            aria-label="Close"
          >
            <CloseIcon className="cv2-icon" />
          </button>
        </header>

        <div className="cv2-drawer__body">
          <div className="cv2-drawer__row">
            <span>Session type</span>
            <span className="cv2-drawer__row-value">
              {session.isDiagnostics
                ? 'Initial Diagnostics'
                : [
                    session.sessionType,
                    session.isOneOffClass ? 'One-off' : null,
                    session.isMakeUpClass ? 'Make-up' : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
            </span>
          </div>

          <h3 className="cv2-drawer__section-title">
            {session.isDiagnostics
              ? 'Diagnostics schedule'
              : 'Attendance · tracked from Student App logins'}
          </h3>

          {session.isDiagnostics && session.students.length === 0 ? (
            <p className="cv2-drawer__empty">There are no students in this diagnostics.</p>
          ) : null}

          {session.isDiagnostics
            ? session.students.map((student) => (
                <div className="cv2-stu" key={student.key}>
                  <div className="cv2-stu__main">
                    <Avatar
                      initials={student.initials}
                      color={student.avatarColor}
                      title={student.name}
                    />
                    <span className="cv2-stu__info">
                      <button
                        type="button"
                        className="cv2-stu__name cv2-stu__name--link"
                        onClick={() => onStudentClick(student)}
                      >
                        {student.name}
                      </button>
                    </span>
                    <span className="cv2-stu__badges">
                      <StatusPill status={student.status} showDot />
                    </span>
                  </div>
                </div>
              ))
            : session.students.map((student) => (
                <StudentCard
                  key={student.key}
                  session={session}
                  student={student}
                  busyActionKey={busyActionKey}
                  onEditStatus={onEditStatus}
                  onToggleMode={onToggleMode}
                  onDefer={onDefer}
                  onOneOff={onOneOff}
                  onToggleFlexi={onToggleFlexi}
                  onReEnrol={onReEnrol}
                  onPause={onPause}
                  onStudentClick={onStudentClick}
                />
              ))}

          {!session.isDiagnostics && session.isLive && notLoggedInCount > 0 ? (
            <div className="cv2-drawer__alert">
              <strong>
                {notLoggedInCount} student{notLoggedInCount === 1 ? '' : 's'} not logged in yet.
              </strong>{' '}
              These will be flagged as Missed once the centre&rsquo;s threshold passes, unless the
              Student App registers a login.
            </div>
          ) : null}

          {session.description ? (
            <>
              <h3 className="cv2-drawer__section-title">Session description</h3>
              <p style={{ fontSize: 12.5, color: '#4a4a4a', margin: 0 }}>{session.description}</p>
            </>
          ) : null}
        </div>

        <footer className="cv2-drawer__actions">
          {session.isDiagnostics ? (
            <button
              type="button"
              className="cv2-segment__btn is-active"
              onClick={() => onTutorClick(session)}
            >
              Open Diagnostics
            </button>
          ) : (
            <button
              type="button"
              className="cv2-segment__btn is-active"
              onClick={() => onEditSession(session)}
              disabled={anyBusy}
            >
              Edit session
            </button>
          )}
          <button type="button" className="cv2-btn" onClick={onClose}>
            Close
          </button>
        </footer>
      </aside>
    </>
  );
}

const studentShape = PropTypes.shape({
  key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string.isRequired,
  initials: PropTypes.string.isRequired,
  avatarColor: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  deliveryMode: PropTypes.string.isRequired,
});

const sessionShape = PropTypes.shape({
  tutorName: PropTypes.string.isRequired,
  date: PropTypes.string,
  students: PropTypes.arrayOf(studentShape).isRequired,
  laneColorIndex: PropTypes.number.isRequired,
});

SessionDrawer.propTypes = {
  session: sessionShape,
  onClose: PropTypes.func.isRequired,
  onEditStatus: PropTypes.func.isRequired,
  onToggleMode: PropTypes.func.isRequired,
  onDefer: PropTypes.func.isRequired,
  onOneOff: PropTypes.func.isRequired,
  onToggleFlexi: PropTypes.func.isRequired,
  onReEnrol: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onEditSession: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
  onTutorClick: PropTypes.func.isRequired,
  busyActionKey: PropTypes.string,
};

SessionDrawer.defaultProps = {
  session: null,
  busyActionKey: null,
};

StudentCard.propTypes = {
  session: sessionShape.isRequired,
  student: studentShape.isRequired,
  busyActionKey: PropTypes.string,
  onEditStatus: PropTypes.func.isRequired,
  onToggleMode: PropTypes.func.isRequired,
  onDefer: PropTypes.func.isRequired,
  onOneOff: PropTypes.func.isRequired,
  onToggleFlexi: PropTypes.func.isRequired,
  onReEnrol: PropTypes.func.isRequired,
  onPause: PropTypes.func.isRequired,
  onStudentClick: PropTypes.func.isRequired,
};

StudentCard.defaultProps = {
  busyActionKey: null,
};
